import { infiniteQueryOptions, keepPreviousData, queryOptions } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import type { RevenueSource, StatsUnit } from '@/shared/api/types';

/** 기간 요약 조건 — 둘 다 비우면 서버 기본(기준일이 속한 달 1일 ~ 기준일) */
export type RevenueSummaryRange = { from?: string; to?: string };

/** 결제 내역 조건 — 기간은 요약과 같은 해석, 수익원은 비우면 전체 */
export type RevenueTransactionFilter = RevenueSummaryRange & { source?: RevenueSource };

/** 결제 내역 한 번에 받는 건수 (서버 최대 50) */
export const TRANSACTIONS_PAGE_SIZE = 20;

export const revenueKeys = {
  all: ['revenue'] as const,
  transactions: (filter: RevenueTransactionFilter) =>
    [...revenueKeys.all, 'transactions', filter] as const,
  summary: (range: RevenueSummaryRange) => [...revenueKeys.all, 'summary', range] as const,
  trend: (params: { from: string; unit: StatsUnit }) =>
    [...revenueKeys.all, 'trend', params] as const,
};

export const revenueSummaryQueryOptions = (range: RevenueSummaryRange = {}) =>
  queryOptions({
    queryKey: revenueKeys.summary(range),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/revenue/summary', { params: { query: range }, signal })),
    placeholderData: keepPreviousData,
  });

export const revenueTrendQueryOptions = (params: { from: string; unit: StatsUnit }) =>
  queryOptions({
    queryKey: revenueKeys.trend(params),
    queryFn: ({ signal }) =>
      unwrap(api.GET('/api/revenue/trend', { params: { query: params }, signal })),
    placeholderData: keepPreviousData,
  });

/**
 * 결제 내역 — 최신순, 커서로 이어 받는다 ("더 보기"). 조건이 바뀌면 키가 바뀌어 처음부터 다시 받는다.
 * 서버가 기준일(asOf)까지만 주므로 같은 기간 요약의 매출 합계와 맞는다.
 */
export const revenueTransactionsQueryOptions = (filter: RevenueTransactionFilter) =>
  infiniteQueryOptions({
    queryKey: revenueKeys.transactions(filter),
    queryFn: ({ pageParam, signal }) =>
      unwrap(
        api.GET('/api/revenue/transactions', {
          params: {
            query: {
              ...filter,
              ...(pageParam ? { cursor: pageParam } : {}),
              limit: TRANSACTIONS_PAGE_SIZE,
            },
          },
          signal,
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: lastPage => lastPage.nextCursor,
    placeholderData: keepPreviousData,
  });
